import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { STORAGE_KEYS } from '#/shared/lib/storage'

interface WorkspaceTabsPrefsState {
  /** Pinned (default) = the tab strip stays open at the top and the dashboard
   *  shifts down to make room. Unpinned = zero-footprint hover flyout. */
  pinned: boolean
  setPinned: (pinned: boolean) => void
}

/**
 * Bumped when the shipped default changes and every device must be re-seeded
 * from it. v1: the strip is pinned open out of the box — hidden behind a hover
 * notch, the feature went unnoticed, so the default became "visible" and the
 * notch became the opt-in. Persist writes the initial state on first load, so
 * every existing device already holds the old `false`; only a migration can
 * move them.
 */
const PREFS_VERSION = 1

/** Kept separate from the tabs store: tabs are SESSION-scoped (cleared on
 *  logout), while this is a DEVICE-scoped UI preference that must survive it. */
export const useWorkspaceTabsPrefsStore = create<WorkspaceTabsPrefsState>()(
  persist(
    (set) => ({
      pinned: true,
      setPinned: (pinned) => set({ pinned }),
    }),
    {
      name: STORAGE_KEYS.workspaceTabsPinned.key,
      storage: createJSONStorage(() => localStorage),
      version: PREFS_VERSION,
      // One-time re-seed to the new default. Anyone who prefers the old
      // zero-space notch unlocks it again and that choice sticks — the version
      // is already current, so this never runs twice.
      migrate: (persisted) => ({
        ...(persisted as WorkspaceTabsPrefsState),
        pinned: true,
      }),
    },
  ),
)
